import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, of, throwError, Subject } from 'rxjs';
import { take, filter, map, shareReplay, switchMap, startWith, takeUntil } from 'rxjs/operators';

// redux
import { setTitle, dispatchAppError, navigate, openPrintDialog, openWindow, setBrowserTitle } from 'src/app/actions/core.actions';
import * as PlaylistActions from 'src/app/actions/playlist.actions';
import { reportAppEvent } from 'src/app/actions/event-log.actions';
import { RootState } from 'src/app/reducers';
import { PlaylistSortType, SubjectId } from 'src/app/resources/config';
import * as PlaylistSelectors from 'src/app/selectors/playlist.selectors';
import * as StaticDataSelectors from '../../../selectors/static-data.selectors';
import { getSignedInUser } from 'src/app/selectors/auth.selectors';
import { getStaticCommonData } from '../../../selectors/static-data.selectors';

// models
import { Playlist } from 'src/app/models/playlist';
import {
  ReadablePlaylistProblem,
  ReadableEnglishPlaylistProblem,
  ReadableSciencePlaylistProblem,
  ReadableNationalLanguagePlaylistProblem,
  ReadableHistoryPlaylistProblem
} from 'src/app/models/problem';
import { User } from 'src/app/models/user';
import { ThemeForDisplay } from 'src/app/models/playlist';
import { AppEvent } from 'src/app/models/event-log';
import { Subject as SubjectModel, University } from 'src/app/models/common-data';
import { PlaylistSearchCondition } from 'src/app/models/playlist-search-condition';
import { PlaylistSearchSortType } from 'src/app/models/playlist-search-query-params';
import { StaticCommonData } from 'src/app/models/static-common-data';

// utils
import { Log } from 'src/app/utils/log';
import { SubjectUtil } from '../../../utils/subject-util';
import { WINDOW_OBJECT } from 'src/app/utils/injection-tokens';

// mapper
import { PlaylistSearchQueryParamsMapper } from 'src/app/mappers/playlist-search-query-params-mapper';

// config & resources
import { RoutingPathResolver } from 'src/app/app-routing-path-resolver';
import { enter } from 'src/app/resources/animations';

// services
import { PrintListenerService } from '../../../services/print-listener.service';

// errors
import { GeneralError } from 'src/app/errors/general-error';
import { CustomErrorMessage } from 'src/app/errors/error-info';

@Component({
  selector: 'app-playlist-detail',
  templateUrl: './playlist-detail.component.html',
  styleUrls: ['./playlist-detail.component.scss'],
  animations: [enter]
})
export class PlaylistDetailComponent implements OnInit, OnDestroy {
  private LOG_SOURCE = this.constructor.name;
  private objectUrls: string[] = [];
  private destroy$ = new Subject<void>();
  private printPoller: ReturnType<typeof setInterval> | null = null;
  private onPrintEndCallback: (() => void) | null = null;

  subjectId$: Observable<string>;
  sortType$: Observable<string>;
  subjects$: Observable<SubjectModel[]>;
  subjectLabelId: string;
  subjectLabelName: string;
  subjectLabelStyle: string;
  playlist$: Observable<Playlist>;
  englishPlaylistThemes$: Observable<ThemeForDisplay<ReadableEnglishPlaylistProblem>[]>;
  mathPlaylistThemes$: Observable<ThemeForDisplay<ReadableSciencePlaylistProblem>[]>;
  nationalLanguagePlaylistThemes$: Observable<ThemeForDisplay<ReadableNationalLanguagePlaylistProblem>[]>;
  physicsPlaylistThemes$: Observable<ThemeForDisplay<ReadableSciencePlaylistProblem>[]>;
  chemistryPlaylistThemes$: Observable<ThemeForDisplay<ReadableSciencePlaylistProblem>[]>;
  biologyPlaylistThemes$: Observable<ThemeForDisplay<ReadableSciencePlaylistProblem>[]>;
  japaneseHistoryPlaylistThemes$: Observable<ThemeForDisplay<ReadableHistoryPlaylistProblem>[]>;
  worldHistoryPlaylistThemes$: Observable<ThemeForDisplay<ReadableHistoryPlaylistProblem>[]>;
  geographyPlaylistThemes$: Observable<ThemeForDisplay<ReadableSciencePlaylistProblem>[]>;
  politicalEconomyPlaylistThemes$: Observable<ThemeForDisplay<ReadableSciencePlaylistProblem>[]>;
  specialAdmissionsPlaylistThemes$: Observable<ThemeForDisplay<ReadableSciencePlaylistProblem>[]>;
  loaded$: Observable<boolean>;
  signedInUser$: Observable<User>;
  isTrial: boolean;

  // テーマごとの印刷用
  isPrintReady = false;
  printMenuType: 'article' | 'spellcheck' | null = null;
  readablePlaylistProblemsByThemeId$: Observable<
    | ThemeForDisplay<ReadableEnglishPlaylistProblem>
    | ThemeForDisplay<ReadableSciencePlaylistProblem>
    | ThemeForDisplay<ReadableNationalLanguagePlaylistProblem>
    | ThemeForDisplay<ReadableHistoryPlaylistProblem>
  >;

  staticCommonData$: Observable<StaticCommonData>;
  universities$: Observable<University[]>;

  constructor(
    private store: Store<RootState>,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    @Inject(WINDOW_OBJECT) private window: Window,
    private printListenerService: PrintListenerService
  ) {}

  ngOnInit() {
    this.setUpUser();
    this.setUpSubjectId();
    this.setUpStaticCommonData();
    this.setUpSortType();
    this.setUpSubjects();
    this.findPlaylistIfNeeded();
    this.setUpPlaylist();
    this.setUpSubjectNameAndStyle();
    this.setUpTitle();
    this.setUpEnglishPlaylistThemes();
    this.setUpMathPlaylistThemes();
    this.setUpNationalLanguagePlaylistThemes();
    this.setUpPhysicsPlaylistThemes();
    this.setUpChemistryPlaylistThemes();
    this.setUpBiologyPlaylistThemes();
    this.setUpJapaneseHistoryPlaylistThemes();
    this.setUpWorldHistoryPlaylistThemes();
    this.setUpGeographyPlaylistThemes();
    this.setUpPoliticalEconomyPlaylistThemes();
    this.setUpSpecialAdmissionsPlaylistThemes();
    this.setUpLoaded();
    this.findPlaylistProblems();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.printPoller) {
      clearInterval(this.printPoller);
      this.printPoller = null;
    }
    this.printListenerService.clearOnPrintEnd(this.onPrintEndCallback);
    this.onPrintEndCallback = null;
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    this.store.dispatch(PlaylistActions.initializePdfObjectUrlState());
    this.store.dispatch(PlaylistActions.initializePlaylistProblemsState());
  }

  backToPreviousView() {
    this.location.back();
  }

  clickTag(tag: string) {
    combineLatest([this.subjectId$, this.sortType$])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(([subjectId, sortType]) => {
        const playlistSortType = this.getPlaylistSortType(sortType);

        let condition: PlaylistSearchCondition = {
          subjectId,
          sortType: playlistSortType
        };

        if (tag) condition = { ...condition, tag };

        const queryParams = PlaylistSearchQueryParamsMapper.encodePlaylistSearchCondition(condition);
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolvePlaylists(), extras: { queryParams } }));
      });
  }

  openThemePdf(event: Event, theme: ThemeForDisplay<ReadablePlaylistProblem>) {
    event.stopPropagation();
    if (!theme.pdfPath) {
      Log.warn(this.LOG_SOURCE, `pdfPath が設定されていません. themeId: `, theme.id);
      return;
    }
    Log.debug(this.LOG_SOURCE, `theme の pdf をダウンロードして開きます. pdfPath: `, theme.pdfPath);
    theme.pdfDownloading = true;
    const pdfPath = theme.pdfPath;
    this.store.dispatch(PlaylistActions.resolvePdfObjectUrl({ pdfPath }));
    this.store
      .select(PlaylistSelectors.getSpecificResolvePdfObjectUrlResult(pdfPath))
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(result => {
        theme.pdfDownloading = false;
        if (result.success) {
          this.store.dispatch(openWindow({ url: result.objectUrl }));
          this.objectUrls.push(result.objectUrl);
          this.store.dispatch(PlaylistActions.initializePdfObjectUrl({ pdfPath }));
        }
      });

    const subjectId = theme.id.slice(0, 2);
    const splitPdfPath = theme.pdfPath.split('/');
    const selectedPlaylistPdf = splitPdfPath[splitPdfPath.length - 1];
    const selectedPlaylistSetName = selectedPlaylistPdf.slice(0, 3);
    const appEvent: AppEvent = {
      type: 'select-playlist-pdf',
      value: JSON.stringify({
        subjectId,
        selectedPlaylistSetName,
        selectedPlaylistPdf
      })
    };
    setTimeout(() => this.store.dispatch(reportAppEvent({ event: appEvent })));
  }

  onPrintPreviewReady() {
    Log.debug(this.LOG_SOURCE, `印刷準備が完了しました。`);
    setTimeout(() => {
      this.isPrintReady = true;
    });
  }

  showPrintDialog(themeId: string, event: { menuType: 'article' | 'spellcheck'; event: MouseEvent }) {
    Log.debug(this.LOG_SOURCE, '選択されたテーマID:', themeId, 'メニュータイプ:', event.menuType);

    // 体験版ユーザーの場合は、アラートを表示して終了
    if (this.isTrial) {
      alert('体験版ユーザーは印刷機能をご利用いただけません。');
      return;
    }

    this.printMenuType = event.menuType;
    // 印刷用に印刷データを科目ごと、テーマIDで絞り込む
    this.readablePlaylistProblemsByThemeId$ = this.playlist$.pipe(
      switchMap(playlist => {
        const source$ =
          playlist.subjectId === SubjectId.ENGLISH
            ? this.englishPlaylistThemes$
            : playlist.subjectId === SubjectId.MATH
            ? this.mathPlaylistThemes$
            : playlist.subjectId === SubjectId.NATIONAL_LANGUAGE
            ? this.nationalLanguagePlaylistThemes$
            : playlist.subjectId === SubjectId.PHYSICS
            ? this.physicsPlaylistThemes$
            : playlist.subjectId === SubjectId.CHEMISTRY
            ? this.chemistryPlaylistThemes$
            : playlist.subjectId === SubjectId.BIOLOGY
            ? this.biologyPlaylistThemes$
            : playlist.subjectId === SubjectId.JAPANESE_HISTORY
            ? this.japaneseHistoryPlaylistThemes$
            : playlist.subjectId === SubjectId.WORLD_HISTORY
            ? this.worldHistoryPlaylistThemes$
            : playlist.subjectId === SubjectId.GEOGRAPHY
            ? this.geographyPlaylistThemes$
            : playlist.subjectId === SubjectId.POLITICAL_ECONOMY
            ? this.politicalEconomyPlaylistThemes$
            : of([]);
        return source$.pipe(
          map(themes => {
            const theme = themes.find(t => t.id === themeId) ?? null;

            if (!theme) return null;
            return {
              ...theme,
              problems:
                theme.problems
                  ?.filter((p, index, arr) => {
                    // 1つ前の problem と id が同じなら除外 (国語のテーマは同一 problemId の問題が複数存在するため、重複を除外する)
                    if (index > 0 && p.id === arr[index - 1].id) {
                      return false;
                    }
                    return true;
                  })
                  ?.filter(p => p.hasExternalData) ?? [] // 問題画像がない問題は印刷できないため、hasExternalData が true の問題のみを印刷対象とする
            };
          })
        );
      })
    );

    this.onPrintEndCallback = () => {
      Log.debug(this.LOG_SOURCE, `印刷完了`);
      this.printMenuType = null;
      this.isPrintReady = false;
    };
    this.printListenerService.onPrintEnd(this.onPrintEndCallback);

    // 印刷準備ができるのを待つ (100msごとにポーリング)
    Log.debug(this.LOG_SOURCE, `印刷準備ができるのを待っています・・・`);
    if (this.printPoller) clearInterval(this.printPoller);
    this.printPoller = setInterval(() => {
      if (this.isPrintReady) {
        if (this.printPoller) {
          clearInterval(this.printPoller);
          this.printPoller = null;
        }
        this.store.dispatch(openPrintDialog());
        this.isPrintReady = false;
      }
    }, 100); // Poll every 100ms
  }

  private setUpUser() {
    this.signedInUser$ = this.store.select(getSignedInUser).pipe(filter((user): user is User => user !== null && user !== 'none'));

    this.signedInUser$.pipe(take(1), takeUntil(this.destroy$)).subscribe(user => {
      this.isTrial = user.isTrial ?? false;
    });
  }

  private setUpSubjectId() {
    this.subjectId$ = this.activatedRoute.queryParamMap.pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      map(queryParamMap => queryParamMap.get('subjectId') || '')
    );
  }

  private setUpStaticCommonData() {
    this.staticCommonData$ = this.store.select(getStaticCommonData).pipe(
      filter(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.universities$ = this.store.select(StaticDataSelectors.getUniversities).pipe(
      filter(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private setUpSortType() {
    this.sortType$ = this.activatedRoute.queryParamMap.pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      map(queryParamMap => queryParamMap.get('sortType') || '')
    );
  }

  private setUpSubjects() {
    this.subjects$ = this.store.select(StaticDataSelectors.getSubject).pipe(
      filter(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private setUpSubjectNameAndStyle() {
    combineLatest([this.subjects$, this.playlist$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([subjects, playlist]) => {
        this.subjectLabelId = playlist.subjectId;
        this.subjectLabelName = SubjectUtil.getName(subjects, playlist.subjectId);
        this.subjectLabelStyle = SubjectUtil.getSubjectLabelStyle(playlist.subjectId);
      });
  }

  private setUpTitle() {
    const subjects$ = this.store.select(StaticDataSelectors.getSubject).pipe(
      filter(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    combineLatest([subjects$, this.playlist$.pipe(filter(it => it != null))])
      .pipe(take(1))
      .subscribe(([subjects, playlist]) => {
        let suffix = SubjectUtil.getName(subjects, playlist.subjectId);
        suffix = suffix === '' ? '' : `- ${suffix}`;
        const title = `おすすめ問題セット ${suffix}`;
        setTimeout(() => {
          this.store.dispatch(setBrowserTitle({ subTitle: title }));
          this.store.dispatch(setTitle({ title }));
        });
      });
  }

  private setUpPlaylist() {
    this.playlist$ = combineLatest([
      this.store.select(PlaylistSelectors.getPlaylists).pipe(filter(it => it != null)),
      this.activatedRoute.paramMap.pipe(map(paramMap => paramMap.get('playlistId') || ''))
    ]).pipe(
      map(([playlists, playlistId]) => {
        const selectedPlaylist = playlists.find(it => it.playlistId === playlistId);
        if (!playlistId || !selectedPlaylist) {
          this.store.dispatch(
            dispatchAppError({
              source: this.LOG_SOURCE,
              error: GeneralError.customMessage(CustomErrorMessage.PLAYLIST_DETAIL_INVALID_PLAYLIST_ID)
            })
          );
          throwError(() => GeneralError.invalidArguments(`指定された playlistId は playlists に存在しません. playlistId: ${playlistId}`));
          return null;
        }
        return selectedPlaylist;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private setUpEnglishPlaylistThemes() {
    this.englishPlaylistThemes$ = combineLatest([
      this.store.select(PlaylistSelectors.getEnglishPlaylistProblems).pipe(filter(it => it != null)),
      this.playlist$.pipe(filter(it => it != null && it.subjectId === SubjectId.ENGLISH))
    ]).pipe(
      switchMap(([englishProblems, playlist]) =>
        this.store.select(PlaylistSelectors.getReadableEnglishPlaylistProblems(englishProblems)).pipe(
          filter(it => it != null),
          map(readableProblems => {
            const themesForDisplay = playlist.themes.map<ThemeForDisplay<ReadableEnglishPlaylistProblem>>(theme => {
              const themeProblems = this.sortPlaylistProblems(readableProblems.filter(it => it.themeId === theme.id));
              return {
                id: theme.id,
                name: theme.name,
                order: theme.order,
                problemCount: themeProblems.length,
                pdfPath: theme.pdfPath,
                pdfDownloading: false,
                problems: themeProblems,
                hasExternalData: themeProblems.some(p => p.hasExternalData)
              };
            });
            const sortedThemes = this.sortTheme(themesForDisplay);
            return sortedThemes;
          })
        )
      )
    );
  }

  private setUpMathPlaylistThemes() {
    this.mathPlaylistThemes$ = combineLatest([
      this.store.select(PlaylistSelectors.getMathPlaylistProblems).pipe(filter(it => it != null)),
      this.playlist$.pipe(filter(it => it != null && it.subjectId === SubjectId.MATH))
    ]).pipe(
      switchMap(([mathProblems, playlist]) =>
        this.store.select(PlaylistSelectors.getReadableMathPlaylistProblems(mathProblems)).pipe(
          filter(it => it != null),
          map(readableProblems => {
            const themesForDisplay = playlist.themes.map<ThemeForDisplay<ReadableSciencePlaylistProblem>>(theme => {
              const themeProblems = this.sortPlaylistProblems(readableProblems.filter(it => it.themeId === theme.id));
              return {
                id: theme.id,
                name: theme.name,
                order: theme.order,
                problemCount: themeProblems.length,
                pdfPath: theme.pdfPath,
                pdfDownloading: false,
                problems: themeProblems,
                hasExternalData: themeProblems.some(p => p.hasExternalData)
              };
            });
            const sortedThemes = this.sortTheme(themesForDisplay);
            return sortedThemes;
          })
        )
      )
    );
  }

  private setUpNationalLanguagePlaylistThemes() {
    this.nationalLanguagePlaylistThemes$ = combineLatest([
      this.store.select(PlaylistSelectors.getNationalLanguagePlaylistProblems).pipe(filter(it => it != null)),
      this.playlist$.pipe(filter(it => it != null && it.subjectId === SubjectId.NATIONAL_LANGUAGE))
    ]).pipe(
      switchMap(([nationalLanguageProblems, playlist]) =>
        this.store.select(PlaylistSelectors.getReadableNationalLanguagePlaylistProblems(nationalLanguageProblems)).pipe(
          filter(it => it != null),
          map(readableProblems => {
            const themesForDisplay = playlist.themes.map<ThemeForDisplay<ReadableNationalLanguagePlaylistProblem>>(theme => {
              const themeProblems = this.sortPlaylistProblems(readableProblems.filter(it => it.themeId === theme.id));
              // 国語の場合は problemId のユニーク数を problemCount とする（同一 problemId が複数存在するため）
              const ids = themeProblems.map(themeProblem => themeProblem.id);
              const problemCount = Array.from(new Set(ids)).length;
              return {
                id: theme.id,
                name: theme.name,
                order: theme.order,
                problemCount,
                pdfPath: theme.pdfPath,
                pdfDownloading: false,
                problems: themeProblems,
                hasExternalData: themeProblems.some(p => p.hasExternalData)
              };
            });
            const sortedThemes = this.sortTheme(themesForDisplay);
            return sortedThemes;
          })
        )
      )
    );
  }

  private setUpPhysicsPlaylistThemes() {
    this.physicsPlaylistThemes$ = combineLatest([
      this.store.select(PlaylistSelectors.getPhysicsPlaylistProblems).pipe(filter(it => it != null)),
      this.playlist$.pipe(filter(it => it != null && it.subjectId === SubjectId.PHYSICS))
    ]).pipe(
      switchMap(([physicsProblems, playlist]) =>
        this.store.select(PlaylistSelectors.getReadablePhysicsPlaylistProblems(physicsProblems)).pipe(
          filter(it => it != null),
          map(readableProblems => {
            const themesForDisplay = playlist.themes.map<ThemeForDisplay<ReadableSciencePlaylistProblem>>(theme => {
              const themeProblems = this.sortPlaylistProblems(readableProblems.filter(it => it.themeId === theme.id));
              return {
                id: theme.id,
                name: theme.name,
                order: theme.order,
                problemCount: themeProblems.length,
                pdfPath: theme.pdfPath,
                pdfDownloading: false,
                problems: themeProblems,
                hasExternalData: themeProblems.some(p => p.hasExternalData)
              };
            });
            const sortedThemes = this.sortTheme(themesForDisplay);
            return sortedThemes;
          })
        )
      )
    );
  }

  private setUpChemistryPlaylistThemes() {
    this.chemistryPlaylistThemes$ = combineLatest([
      this.store.select(PlaylistSelectors.getChemistryPlaylistProblems).pipe(filter(it => it != null)),
      this.playlist$.pipe(filter(it => it != null && it.subjectId === SubjectId.CHEMISTRY))
    ]).pipe(
      switchMap(([chemistryProblems, playlist]) =>
        this.store.select(PlaylistSelectors.getReadableChemistryPlaylistProblems(chemistryProblems)).pipe(
          filter(it => it != null),
          map(readableProblems => {
            const themesForDisplay = playlist.themes.map<ThemeForDisplay<ReadableSciencePlaylistProblem>>(theme => {
              const themeProblems = this.sortPlaylistProblems(readableProblems.filter(it => it.themeId === theme.id));
              return {
                id: theme.id,
                name: theme.name,
                order: theme.order,
                problemCount: themeProblems.length,
                pdfPath: theme.pdfPath,
                pdfDownloading: false,
                problems: themeProblems,
                hasExternalData: themeProblems.some(p => p.hasExternalData)
              };
            });
            const sortedThemes = this.sortTheme(themesForDisplay);
            return sortedThemes;
          })
        )
      )
    );
  }

  private setUpBiologyPlaylistThemes() {
    this.biologyPlaylistThemes$ = combineLatest([
      this.store.select(PlaylistSelectors.getBiologyPlaylistProblems).pipe(filter(it => it != null)),
      this.playlist$.pipe(filter(it => it != null && it.subjectId === SubjectId.BIOLOGY))
    ]).pipe(
      switchMap(([biologyProblems, playlist]) =>
        this.store.select(PlaylistSelectors.getReadableBiologyPlaylistProblems(biologyProblems)).pipe(
          filter(it => it != null),
          map(readableProblems => {
            const themesForDisplay = playlist.themes.map<ThemeForDisplay<ReadableSciencePlaylistProblem>>(theme => {
              const themeProblems = this.sortPlaylistProblems(readableProblems.filter(it => it.themeId === theme.id));
              return {
                id: theme.id,
                name: theme.name,
                order: theme.order,
                problemCount: themeProblems.length,
                pdfPath: theme.pdfPath,
                pdfDownloading: false,
                problems: themeProblems,
                hasExternalData: themeProblems.some(p => p.hasExternalData)
              };
            });
            const sortedThemes = this.sortTheme(themesForDisplay);
            return sortedThemes;
          })
        )
      )
    );
  }

  private setUpJapaneseHistoryPlaylistThemes() {
    this.japaneseHistoryPlaylistThemes$ = combineLatest([
      this.store.select(PlaylistSelectors.getJapaneseHistoryPlaylistProblems).pipe(filter(it => it != null)),
      this.playlist$.pipe(filter(it => it != null && it.subjectId === SubjectId.JAPANESE_HISTORY))
    ]).pipe(
      switchMap(([japaneseHistoryProblems, playlist]) =>
        this.store.select(PlaylistSelectors.getReadableJapaneseHistoryPlaylistProblems(japaneseHistoryProblems)).pipe(
          filter(it => it != null),
          map(readableProblems => {
            const themesForDisplay = playlist.themes.map<ThemeForDisplay<ReadableHistoryPlaylistProblem>>(theme => {
              const themeProblems = this.sortPlaylistProblems(readableProblems.filter(it => it.themeId === theme.id));
              return {
                id: theme.id,
                name: theme.name,
                order: theme.order,
                problemCount: themeProblems.length,
                pdfPath: theme.pdfPath,
                pdfDownloading: false,
                problems: themeProblems,
                hasExternalData: themeProblems.some(p => p.hasExternalData)
              };
            });
            const sortedThemes = this.sortTheme(themesForDisplay);
            return sortedThemes;
          })
        )
      )
    );
  }

  private setUpWorldHistoryPlaylistThemes() {
    this.worldHistoryPlaylistThemes$ = combineLatest([
      this.store.select(PlaylistSelectors.getWorldHistoryPlaylistProblems).pipe(filter(it => it != null)),
      this.playlist$.pipe(filter(it => it != null && it.subjectId === SubjectId.WORLD_HISTORY))
    ]).pipe(
      switchMap(([worldHistoryProblems, playlist]) =>
        this.store.select(PlaylistSelectors.getReadableWorldHistoryPlaylistProblems(worldHistoryProblems)).pipe(
          filter(it => it != null),
          map(readableProblems => {
            const themesForDisplay = playlist.themes.map<ThemeForDisplay<ReadableHistoryPlaylistProblem>>(theme => {
              const themeProblems = this.sortPlaylistProblems(readableProblems.filter(it => it.themeId === theme.id));
              return {
                id: theme.id,
                name: theme.name,
                order: theme.order,
                problemCount: themeProblems.length,
                pdfPath: theme.pdfPath,
                pdfDownloading: false,
                problems: themeProblems,
                hasExternalData: themeProblems.some(p => p.hasExternalData)
              };
            });
            const sortedThemes = this.sortTheme(themesForDisplay);
            return sortedThemes;
          })
        )
      )
    );
  }

  private setUpGeographyPlaylistThemes() {
    this.geographyPlaylistThemes$ = combineLatest([
      this.store.select(PlaylistSelectors.getGeographyPlaylistProblems).pipe(filter(it => it != null)),
      this.playlist$.pipe(filter(it => it != null && it.subjectId === SubjectId.GEOGRAPHY))
    ]).pipe(
      switchMap(([geographyProblems, playlist]) =>
        this.store.select(PlaylistSelectors.getReadableGeographyPlaylistProblems(geographyProblems)).pipe(
          filter(it => it != null),
          map(readableProblems => {
            const themesForDisplay = playlist.themes.map<ThemeForDisplay<ReadableSciencePlaylistProblem>>(theme => {
              const themeProblems = this.sortPlaylistProblems(readableProblems.filter(it => it.themeId === theme.id));
              return {
                id: theme.id,
                name: theme.name,
                order: theme.order,
                problemCount: themeProblems.length,
                pdfPath: theme.pdfPath,
                pdfDownloading: false,
                problems: themeProblems,
                hasExternalData: themeProblems.some(p => p.hasExternalData)
              };
            });
            const sortedThemes = this.sortTheme(themesForDisplay);
            return sortedThemes;
          })
        )
      )
    );
  }

  private setUpPoliticalEconomyPlaylistThemes() {
    this.politicalEconomyPlaylistThemes$ = combineLatest([
      this.store.select(PlaylistSelectors.getPoliticalEconomyPlaylistProblems).pipe(filter(it => it != null)),
      this.playlist$.pipe(filter(it => it != null && it.subjectId === SubjectId.POLITICAL_ECONOMY))
    ]).pipe(
      switchMap(([politicalEconomyProblems, playlist]) =>
        this.store.select(PlaylistSelectors.getReadablePoliticalEconomyPlaylistProblems(politicalEconomyProblems)).pipe(
          filter(it => it != null),
          map(readableProblems => {
            const themesForDisplay = playlist.themes.map<ThemeForDisplay<ReadableSciencePlaylistProblem>>(theme => {
              const themeProblems = this.sortPlaylistProblems(readableProblems.filter(it => it.themeId === theme.id));
              return {
                id: theme.id,
                name: theme.name,
                order: theme.order,
                problemCount: themeProblems.length,
                pdfPath: theme.pdfPath,
                pdfDownloading: false,
                problems: themeProblems,
                hasExternalData: themeProblems.some(p => p.hasExternalData)
              };
            });
            const sortedThemes = this.sortTheme(themesForDisplay);
            return sortedThemes;
          })
        )
      )
    );
  }

  private setUpSpecialAdmissionsPlaylistThemes() {
    this.specialAdmissionsPlaylistThemes$ = combineLatest([
      this.store.select(PlaylistSelectors.getSpecialAdmissionsPlaylistProblems).pipe(filter(it => it != null)),
      this.playlist$.pipe(filter(it => it != null && it.subjectId === SubjectId.SPECIAL_ADMISSIONS))
    ]).pipe(
      switchMap(([specialAdmissionsProblems, playlist]) =>
        this.store.select(PlaylistSelectors.getReadableSpecialAdmissionsPlaylistProblems(specialAdmissionsProblems)).pipe(
          filter(it => it != null),
          map(readableProblems => {
            const themesForDisplay = playlist.themes.map<ThemeForDisplay<ReadableSciencePlaylistProblem>>(theme => {
              const themeProblems = this.sortPlaylistProblems(readableProblems.filter(it => it.themeId === theme.id));
              return {
                id: theme.id,
                name: theme.name,
                order: theme.order,
                problemCount: themeProblems.length,
                pdfPath: theme.pdfPath,
                pdfDownloading: false,
                problems: themeProblems,
                hasExternalData: themeProblems.some(p => p.hasExternalData)
              };
            });
            const sortedThemes = this.sortTheme(themesForDisplay);
            return sortedThemes;
          })
        )
      )
    );
  }

  private setUpLoaded() {
    this.loaded$ = combineLatest([
      this.englishPlaylistThemes$.pipe(startWith(null as ThemeForDisplay<ReadableEnglishPlaylistProblem>)),
      this.mathPlaylistThemes$.pipe(startWith(null as ThemeForDisplay<ReadableSciencePlaylistProblem>)),
      this.nationalLanguagePlaylistThemes$.pipe(startWith(null as ThemeForDisplay<ReadableNationalLanguagePlaylistProblem>)),
      this.physicsPlaylistThemes$.pipe(startWith(null as ThemeForDisplay<ReadableSciencePlaylistProblem>)),
      this.chemistryPlaylistThemes$.pipe(startWith(null as ThemeForDisplay<ReadableSciencePlaylistProblem>)),
      this.biologyPlaylistThemes$.pipe(startWith(null as ThemeForDisplay<ReadableSciencePlaylistProblem>)),
      this.japaneseHistoryPlaylistThemes$.pipe(startWith(null as ThemeForDisplay<ReadableHistoryPlaylistProblem>)),
      this.worldHistoryPlaylistThemes$.pipe(startWith(null as ThemeForDisplay<ReadableHistoryPlaylistProblem>)),
      this.geographyPlaylistThemes$.pipe(startWith(null as ThemeForDisplay<ReadableSciencePlaylistProblem>)),
      this.politicalEconomyPlaylistThemes$.pipe(startWith(null as ThemeForDisplay<ReadableSciencePlaylistProblem>)),
      this.specialAdmissionsPlaylistThemes$.pipe(startWith(null as ThemeForDisplay<ReadableSciencePlaylistProblem>))
    ]).pipe(
      map(themes => themes.some(it => it != null)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private findPlaylistIfNeeded() {
    this.store
      .select(PlaylistSelectors.getPlaylists)
      .pipe(take(1))
      .subscribe(playlists => {
        if (!playlists) this.store.dispatch(PlaylistActions.findPlaylists());
      });
  }

  private findPlaylistProblems() {
    this.playlist$
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(playlist => {
        const supportedSubjectIds: string[] = Object.values(SubjectId);
        if (!supportedSubjectIds.includes(playlist.subjectId)) {
          this.store.dispatch(
            dispatchAppError({
              source: this.LOG_SOURCE,
              error: GeneralError.unknown(`不明な科目の playlist が指定されました: ${JSON.stringify(playlist)}`)
            })
          );
          return;
        }
        this.store.dispatch(PlaylistActions.findPlaylistProblems({ subjectId: playlist.subjectId, playlistId: playlist.playlistId }));
      });
  }

  private sortPlaylistProblems<T extends ReadablePlaylistProblem>(problems: T[]): T[] {
    return [...problems].sort((a, b) => {
      if (a.orderInTheme < b.orderInTheme) return -1;
      if (a.orderInTheme > b.orderInTheme) return 1;
      return 0;
    });
  }

  private sortTheme<T extends ReadablePlaylistProblem>(themes: ThemeForDisplay<T>[]): ThemeForDisplay<T>[] {
    return [...themes].sort((a, b) => {
      if (a.order < b.order) return -1;
      if (a.order > b.order) return 1;
      return 0;
    });
  }

  private getPlaylistSortType(sortType: string): PlaylistSortType {
    let playlistSortType: PlaylistSortType;

    if (sortType === PlaylistSearchSortType.PUBLISHED_DATE) {
      playlistSortType = PlaylistSortType.PUBLISHED_DATE;
    } else if (sortType === PlaylistSearchSortType.PLAYLIST_ID_ASC) {
      playlistSortType = PlaylistSortType.PLAYLIST_ID_ASC;
    } else if (sortType === PlaylistSearchSortType.PLAYLIST_ID_DESC) {
      playlistSortType = PlaylistSortType.PLAYLIST_ID_DESC;
    }

    return playlistSortType;
  }
}
